/**
 * FieldRenderer — bridges ConnectorFieldSchema (or a synthetic spec) to a UI control.
 *
 * Simple types (text, password, number, textarea, boolean, select) are rendered
 * via shadcn primitives.  Complex wizard field types reuse existing wizard field
 * components by name.  Synthetic types (date_range, queries) delegate to the
 * dedicated Task 7/8 components.  Hidden / file / json render nothing.
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { HelpCircle, ShieldCheck } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChipMultiSelectDynamic } from '@/components/connectors/form/ChipMultiSelectDynamic';
import { OAuthPopulatedDropdownField } from '@/components/connectors/wizard/dynamic/fields/OAuthPopulatedDropdownField';
import { MultiSelectCheckboxField } from '@/components/connectors/wizard/dynamic/fields/MultiSelectCheckboxField';
import { LoadAdvertisersButton, type Advertiser } from '@/components/connectors/form/LoadAdvertisersButton';
import { SelectDynamic } from '@/components/connectors/form/SelectDynamic';
import { loaderHasSelection } from '@/components/connectors/wizard/dynamic/DynamicField';
import { PASSWORD_MANAGER_OPT_OUT } from '@/components/connectors/shared/passwordManagerOptOut';
import { OAuthRow } from './fields/OAuthRow';
import { QueriesField } from './fields/QueriesField';
import { titleCaseLabel } from './labels';
import { declaredOptionChange } from '@/lib/declaredOptionChange';
import type { ConnectorFieldSchema } from '@/types/connector-schema';
import type { QueryFile } from '@/types/connectors';

// Field types whose wizard-component already renders its own label + help text.
// For these, FieldRenderer must NOT wrap them with an additional Label/help paragraph.
const SELF_LABELED_TYPES = new Set([
  'oauth_button',
  'oauth_populated_dropdown',
  'multi_select_checkbox',
  'multi_select_checkbox_dynamic',
  'load_advertisers_button',
]);

// ---------------------------------------------------------------------------
// Synthetic spec — date_range or queries rows injected by sections.ts
// ---------------------------------------------------------------------------

interface SyntheticSpec {
  type: 'queries';
  label: string;
  hint?: string;
}

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

interface CommonProps {
  value: unknown;
  onChange: (v: unknown) => void;
  stored?: boolean;
  disabled?: boolean;
  /** Full form values map — required for fields whose visibility/options depend on other fields' values. */
  allValues?: Record<string, unknown>;
  /** Atomic multi-field update — required for OAuth fields that populate companion fields (e.g. HubSpot portal_id). */
  onBatchFieldChange?: (updates: Record<string, unknown>) => void;
  /** True when this specific field has unsaved changes. */
  dirty?: boolean;
  /** Validation message for this field, computed by SectionedConfigurationForm (#1163).
   *  Only fields the save actually sends (dirty ∩ visible) are ever validated. */
  error?: string;
  /** Connector-level credential flags (so OAuthRow can derive connected state). */
  credentialFlags?: Record<string, boolean>;
  /** Identifier ('user@example.com' / 'Portal 123' / etc.) for OAuthRow. Backend #140. */
  oauthIdentity?: string | null;
  /** Called by OAuthRow after a successful Reconnect to refetch connector details. */
  onReconnected?: () => void;
  /** Connector name (e.g. "My Google Ads"). Needed for stored-creds OAuth dropdown fetches (#143). */
  connectorName?: string;
  /** Runtime cache of advertiser lists returned by LOAD_ADVERTISERS_BUTTON fields, keyed by loader field name. */
  loaderResults?: Record<string, Advertiser[]>;
  /** Callback fired when a LOAD_ADVERTISERS_BUTTON completes a fetch. */
  onLoaderResult?: (fieldName: string, advertisers: Advertiser[]) => void;
}

// Discriminated union: either a schema field or a synthetic spec.

interface SchemaProps extends CommonProps {
  field: ConnectorFieldSchema;
  synthetic?: undefined;
  name?: undefined;
  connectorType?: string;
}

interface SyntheticProps extends CommonProps {
  field?: undefined;
  synthetic: SyntheticSpec;
  name: string;
  connectorType?: string;
}

export type FieldRendererProps = SchemaProps | SyntheticProps;

// ---------------------------------------------------------------------------
// Top-level dispatcher
// ---------------------------------------------------------------------------

export function FieldRenderer(props: FieldRendererProps) {
  if (props.synthetic) return <SyntheticRow {...props} />;
  return <SchemaRow {...props} />;
}

// ---------------------------------------------------------------------------
// Schema field row (label + control + help text)
// ---------------------------------------------------------------------------

function SchemaRow(props: SchemaProps) {
  const { field } = props;
  const id = `field-${field.name}`;
  const errorId = `${id}-error`;
  const { value, onChange, stored, disabled, dirty, error } = props;

  const errorNode = error ? (
    <p id={errorId} role="alert" className="text-xs text-destructive">
      {error}
    </p>
  ) : null;

  // Wizard field components render their own labels and help text. Bypass the
  // outer wrapper for those to avoid doubled labels — but still surface the
  // error, since a required multi_select_checkbox can produce one (#1163).
  if (SELF_LABELED_TYPES.has(field.type)) {
    return (
      <div className="flex flex-col gap-1.5">
        <Control id={id} errorId={errorId} {...props} />
        {errorNode}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className="text-[13px] font-medium">
          {field.label}
          {field.required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
        {/* #1163 — the schema's `tooltip` used to render on the create wizard only
            (wizard/dynamic/fields/NumberField), so #1096's rewritten ceiling copy was
            invisible on the surface operators actually edit. The wizard hangs its
            tooltip off a bare aria-hidden icon; a <button> with an aria-label is the
            keyboard-reachable equivalent. */}
        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Help for ${field.label}`}
                  className="cursor-help text-muted-foreground"
                >
                  <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {stored && (
          <Badge variant="default" className="h-[18px] bg-success text-success-foreground">
            <ShieldCheck className="mr-1 h-2.5 w-2.5" /> Stored
          </Badge>
        )}
        {dirty && (
          <Badge variant="default" className="h-[18px] bg-[oklch(85%_0.13_75)] text-[oklch(35%_0.13_75)]">
            Edited
          </Badge>
        )}
      </div>
      <Control
        id={id}
        errorId={errorId}
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        stored={stored}
        dirty={dirty}
        error={error}
        // #1999: the loader slice has to cross this boundary too. Everything
        // loader-backed used to be SELF_LABELED (the branch above spreads all
        // of props), so this narrow list was complete. `select_dynamic` is
        // labelled like an ordinary row AND reads the loader's results -- the
        // first field to be both -- and without this it renders a permanently
        // empty picker no matter what the loader returns.
        allValues={props.allValues}
        loaderResults={props.loaderResults}
        onLoaderResult={props.onLoaderResult}
      />
      {errorNode}
      {!error && field.help_text && (
        <p className="text-xs text-muted-foreground">{field.help_text}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Control — selects the right primitive or wizard component
// ---------------------------------------------------------------------------

function Control({
  id,
  errorId,
  field,
  value,
  onChange,
  disabled,
  stored,
  dirty,
  error,
  allValues: allValuesProp,
  onBatchFieldChange,
  credentialFlags,
  oauthIdentity,
  onReconnected,
  connectorType,
  connectorName,
  loaderResults,
  onLoaderResult,
}: { id: string; errorId: string } & SchemaProps) {
  // Fall back to empty map so TypeScript is happy and callers that don't pass
  // allValues (simple text/password rows etc.) still work without errors.
  const allValues: Record<string, unknown> = allValuesProp ?? {};

  // Applied to the primitive inputs only. The composite wizard components own
  // their own markup and read no error prop; their message renders below them
  // via SchemaRow's errorNode. (#1163)
  const invalid = error
    ? { 'aria-invalid': true as const, 'aria-describedby': errorId }
    : {};

  switch (field.type) {
    case 'text':
      return (
        <Input
          id={id}
          {...invalid}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'password':
      return (
        <Input
          id={id}
          type="password"
          {...invalid}
          {...PASSWORD_MANAGER_OPT_OUT}
          value={(value as string) ?? ''}
          placeholder={stored ? '••••••••  Leave blank to keep stored value' : undefined}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <Input
          id={id}
          type="number"
          {...invalid}
          // Empty stays empty (mirrors the wizard's NumberField). `Number('')` is 0,
          // which silently turns "cleared" into an in-band value that a min_value=1
          // bound then rejects — and that used to be PATCHed as a real 0. (#1163)
          value={value === '' || value === null || value === undefined ? '' : String(value)}
          min={field.min_value}
          max={field.max_value}
          step={field.step}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange('');
              return;
            }
            const parsed = Number(raw);
            onChange(Number.isNaN(parsed) ? raw : parsed);
          }}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <Textarea
          id={id}
          {...invalid}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'boolean':
      return (
        <Switch
          id={id}
          checked={!!value}
          onCheckedChange={(c) => onChange(c)}
          disabled={disabled}
        />
      );

    case 'select': {
      // Some backends store the saved value with a narrower type than the
      // SelectOption.value strings (e.g. Meta Ads' time_increment is
      // `Literal[1, 7, "monthly", "all_days"]` — int 1/7 vs string "1"/"7").
      // Radix Select uses strict equality, so int 1 won't match SelectItem
      // value "1" and the trigger renders the placeholder. Coerce to string
      // here so the trigger resolves correctly on read.
      const selectValue =
        value === undefined || value === null ? '' : String(value);
      // #2044 -- drop the '' Radix echoes when a value reaches this select from
      // outside the control; see declaredOptionChange.
      return (
        <Select
          value={selectValue}
          onValueChange={declaredOptionChange(field.options, onChange)}
          disabled={disabled}
        >
          <SelectTrigger id={id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {titleCaseLabel(o.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    case 'multi_select_checkbox':
      return (
        <MultiSelectCheckboxField
          field={field}
          value={value}
          onChange={onChange}
          allValues={allValues}
          readOnly={disabled}
          dirty={dirty}
        />
      );

    case 'multi_select_checkbox_dynamic': {
      // MULTI_SELECT_CHECKBOX_DYNAMIC reads its options from the runtime
      // result of a companion LOAD_ADVERTISERS_BUTTON, referenced by name
      // via `loader_field`. Same contract the wizard uses (DynamicField.tsx).
      const loaderName = field.loader_field ?? '';
      const options = loaderResults?.[loaderName] ?? [];
      return (
        <ChipMultiSelectDynamic
          fieldName={field.name}
          options={options}
          value={Array.isArray(value) ? (value as number[]) : []}
          onChange={(next) => onChange(next)}
          label={field.label}
          dirty={dirty}
          disabled={disabled}
          help_text={field.help_text}
        />
      );
    }

    case 'oauth_button':
      return (
        <OAuthRow
          field={field}
          value={value}
          onChange={onChange}
          connected={!!(credentialFlags?.has_refresh_token || credentialFlags?.has_access_token)}
          identity={oauthIdentity ?? null}
          onReconnected={onReconnected ?? (() => {})}
          dirty={dirty}
          disabled={disabled}
          connectorName={connectorName}
        />
      );

    case 'oauth_populated_dropdown': {
      const isConnected = !!(credentialFlags?.has_refresh_token || credentialFlags?.has_access_token);

      if (isConnected && connectorName) {
        // Editor mode — server looks up the saved credential via connector_name.
        return (
          <OAuthPopulatedDropdownField
            field={field}
            value={value}
            onChange={onChange}
            allValues={allValues}
            onBatchFieldChange={onBatchFieldChange}
            readOnly={disabled}
            connectorType={connectorType}
            connectorName={connectorName}
          />
        );
      }

      // Wizard mode (fresh creds in form state).
      return (
        <OAuthPopulatedDropdownField
          field={field}
          value={value}
          onChange={onChange}
          allValues={allValues}
          onBatchFieldChange={onBatchFieldChange}
          readOnly={disabled}
        />
      );
    }

    case 'load_advertisers_button':
      return (
        <LoadAdvertisersButton
          fieldName={field.name}
          endpoint={field.loader_config?.endpoint ?? ''}
          credentialFields={field.loader_config?.credential_fields ?? []}
          formValues={allValues as Record<string, string>}
          onLoaded={(advertisers) => onLoaderResult?.(field.name, advertisers)}
          hasSelection={loaderHasSelection(field.loader_config, allValues)}
          connectorName={connectorName}
          sendsConnectorName={field.loader_config?.sends_connector_name}
        />
      );

    // #1999: single-valued sibling of multi_select_checkbox_dynamic.
    case 'select_dynamic': {
      const loaderName = field.loader_field ?? '';
      return (
        <SelectDynamic
          fieldName={field.name}
          options={loaderResults?.[loaderName] ?? []}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={onChange}
          emptyLabel={field.placeholder || 'No filter'}
          id={id}
          disabled={disabled}
          {...invalid}
        />
      );
    }

    case 'hidden':
    case 'file':
    case 'json':
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Synthetic field row (date_range / queries)
// ---------------------------------------------------------------------------

function SyntheticRow({
  synthetic,
  name,
  value,
  onChange,
  disabled,
  connectorType,
}: SyntheticProps) {
  const id = `field-${name}`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-[13px] font-medium">
        {synthetic.label}
      </Label>
      {synthetic.type === 'queries' && (
        <QueriesField
          connectorType={connectorType ?? ''}
          value={value as QueryFile | null}
          onChange={(v) => onChange(v)}
          disabled={!!disabled}
        />
      )}
      {synthetic.hint && (
        <p className="text-xs text-muted-foreground">{synthetic.hint}</p>
      )}
    </div>
  );
}
